import React from "react";
import apiClient from "../../services/apiClient";
import { getStoredUsername } from "../../utils/auth";
import { isMobileViewport } from "../../utils/responsive";
import EmptyState from "../common/empty_state";

const getColorTag = (exchange) => {
    switch (exchange) {
        case "binance":
            return "bg-warning-subtle text-warning";
        case "upbit":
            return "bg-brand-subtle text-brand-light";
        case "coinone":
            return "bg-gain-subtle text-gain";
        case "bithumb":
            return "bg-warning-subtle text-warning-light";
        default:
            return "bg-surface-elevated text-content-muted";
    }
};

class APIKeyEditable extends React.Component {
    IDLE_TIMEOUT_MS = 5 * 60 * 1000;
    idleTimer = null;

    constructor(props) {
        super(props);
        this.state = {
            listKeyPair: [],
            // Add modal (new key only)
            isAddModalVisible: false,
            enableFiat: true,
            enableRisky: true,
            addFormValues: {
                strategyName: "",
                fiatExchange: "",
                fiatApiKey: "",
                fiatSecretKey: "",
                riskyExchange: "binance",
                riskyApiKey: "",
                riskySecretKey: "",
            },
            // Inline edit
            editingRowId: null,
            editValues: {},
            // Secrets
            showSecrets: {},
            secretData: null,
            fetchingSecrets: false,
            // UI
            isSaving: false,
            isLoading: true,
            notice: null,
            // Delete confirmation
            deleteConfirmId: null,
        };
    }

    componentDidMount() {
        this.loadKeyPairs();
        window.addEventListener("mousemove", this.resetIdleTimer);
        window.addEventListener("keydown", this.resetIdleTimer);
        // visibilitychange는 MDN 권장대로 document에 등록.
        // 탭 숨김 시 즉시 secret 마스킹 (shoulder-surfing/화면공유 방어 — M2).
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }

    componentWillUnmount() {
        window.removeEventListener("mousemove", this.resetIdleTimer);
        window.removeEventListener("keydown", this.resetIdleTimer);
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        clearTimeout(this.idleTimer);
        this.idleTimer = null;
    }

    handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            this.clearSecrets();
        } else {
            this.resetIdleTimer();
        }
    };

    resetIdleTimer = () => {
        if (this.idleTimer) clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(this.clearSecrets, this.IDLE_TIMEOUT_MS);
    };

    clearSecrets = () => {
        this.setState({ secretData: null, showSecrets: {} });
        this.idleTimer = null;
    };

    loadKeyPairs = () => {
        const username = getStoredUsername();
        if (!username) {
            this.setState({ isLoading: false });
            return;
        }

        this.setState({ isLoading: true });
        apiClient
            .get(`/api/exchange_key_pair/${username}`)
            .then((res) => {
                this.setState({ listKeyPair: res.data, secretData: null, showSecrets: {}, isLoading: false });
            })
            .catch((err) => {
                console.error(err);
                this.setState({
                    isLoading: false,
                    notice: { type: "error", message: "데이터 로드 실패" }
                });
            });
    };

    fetchSecrets = (rowId) => {
        const { secretData } = this.state;
        if (secretData) {
            this.setState((prev) => ({
                showSecrets: { ...prev.showSecrets, [rowId]: !prev.showSecrets[rowId] },
            }));
            return;
        }

        const username = getStoredUsername();
        if (!username) return;

        this.setState({ fetchingSecrets: true });
        apiClient
            .get(`/api/exchange_key_pair/${username}?show_secrets=true`)
            .then((res) => {
                const dataMap = {};
                res.data.forEach((row) => {
                    dataMap[row.id] = row;
                });
                this.setState((prev) => ({
                    secretData: dataMap,
                    showSecrets: { ...prev.showSecrets, [rowId]: true },
                    fetchingSecrets: false,
                }));
                this.resetIdleTimer();
            })
            .catch(() => {
                this.setState({
                    notice: { type: "error", message: "비밀값 조회 실패" },
                    fetchingSecrets: false
                });
            });
    };

    getDisplayValue = (row, field) => {
        const { showSecrets, secretData } = this.state;
        if (showSecrets[row.id] && secretData && secretData[row.id]) {
            return secretData[row.id][field] || "-";
        }
        return row[field] || "-";
    };

    // --- Add Modal ---
    openAddModal = () => {
        this.setState({
            isAddModalVisible: true,
            notice: null,
            enableFiat: true,
            enableRisky: true,
            addFormValues: {
                strategyName: "",
                fiatExchange: "",
                fiatApiKey: "",
                fiatSecretKey: "",
                riskyExchange: "binance",
                riskyApiKey: "",
                riskySecretKey: "",
            },
        });
    };

    closeAddModal = () => {
        this.setState({ isAddModalVisible: false });
    };

    handleAddChange = (field) => (event) => {
        const value = event.target.value;
        this.setState((prev) => ({
            addFormValues: { ...prev.addFormValues, [field]: value },
        }));
    };

    handleAddSubmit = () => {
        const username = getStoredUsername();
        if (!username) {
            this.setState({ notice: { type: "error", message: "로그인 정보를 확인할 수 없습니다." } });
            return;
        }

        const { addFormValues, enableFiat, enableRisky } = this.state;
        if (!enableFiat && !enableRisky) {
            this.setState({ notice: { type: "error", message: "현물 또는 선물 거래소 중 하나 이상을 활성화하세요." } });
            return;
        }
        if (!addFormValues.strategyName.trim()) {
            this.setState({ notice: { type: "error", message: "전략 이름을 입력하세요." } });
            return;
        }
        if (enableFiat && !addFormValues.fiatExchange) {
            this.setState({ notice: { type: "error", message: "현물 거래소를 선택하세요." } });
            return;
        }
        if (enableRisky && !addFormValues.riskyExchange) {
            this.setState({ notice: { type: "error", message: "선물 거래소를 선택하세요." } });
            return;
        }

        const payload = {
            user_id: username,
            strategy_name: addFormValues.strategyName.trim(),
            risky_exchange: enableRisky ? addFormValues.riskyExchange : "",
            risky_api_key: enableRisky ? addFormValues.riskyApiKey : "",
            risky_secret_key: enableRisky ? addFormValues.riskySecretKey : "",
            fiat_exchange: enableFiat ? addFormValues.fiatExchange : "",
            fiat_api_key: enableFiat ? addFormValues.fiatApiKey : "",
            fiat_secret_key: enableFiat ? addFormValues.fiatSecretKey : "",
        };

        this.setState({ isSaving: true });
        apiClient
            .post(`/api/exchange_key_pair`, payload)
            .then(() => {
                this.setState({ notice: { type: "success", message: "API Key 추가 완료" } });
                this.loadKeyPairs();
                this.closeAddModal();
            })
            .catch(() => {
                this.setState({ notice: { type: "error", message: "API Key 추가 실패" } });
            })
            .finally(() => {
                this.setState({ isSaving: false });
            });
    };

    // --- Inline Edit ---
    startEdit = (row) => {
        this.setState({
            editingRowId: row.id,
            editValues: {
                strategy_name: row.strategy_name || "",
                fiat_exchange: row.fiat_exchange || "",
                risky_exchange: row.risky_exchange || "",
                fiat_api_key: "",
                fiat_secret_key: "",
                risky_api_key: "",
                risky_secret_key: "",
            },
        });
    };

    cancelEdit = () => {
        this.setState({ editingRowId: null, editValues: {} });
    };

    handleEditChange = (field) => (event) => {
        const value = event.target.value;
        this.setState((prev) => ({
            editValues: { ...prev.editValues, [field]: value },
        }));
    };

    saveEdit = () => {
        const username = getStoredUsername();
        if (!username) return;

        const { editingRowId, editValues } = this.state;
        if (!editValues.strategy_name.trim()) {
            this.setState({ notice: { type: "error", message: "전략 이름을 입력하세요." } });
            return;
        }
        if (!editValues.fiat_exchange && !editValues.risky_exchange) {
            this.setState({ notice: { type: "error", message: "현물 또는 선물 거래소 중 하나 이상 필요합니다." } });
            return;
        }

        const payload = {
            user_id: username,
            id: editingRowId,
            strategy_name: editValues.strategy_name.trim(),
            risky_exchange: editValues.risky_exchange || "",
            risky_api_key: editValues.risky_api_key || "",
            risky_secret_key: editValues.risky_secret_key || "",
            fiat_exchange: editValues.fiat_exchange || "",
            fiat_api_key: editValues.fiat_api_key || "",
            fiat_secret_key: editValues.fiat_secret_key || "",
        };

        this.setState({ isSaving: true });
        apiClient
            .post(`/api/exchange_key_pair`, payload)
            .then(() => {
                this.setState({ notice: { type: "success", message: "수정 완료" }, editingRowId: null, editValues: {} });
                this.loadKeyPairs();
            })
            .catch(() => {
                this.setState({ notice: { type: "error", message: "수정 실패" } });
            })
            .finally(() => {
                this.setState({ isSaving: false });
            });
    };

    // --- Delete ---
    confirmDelete = (rowId) => {
        this.setState({ deleteConfirmId: rowId });
    };

    cancelDelete = () => {
        this.setState({ deleteConfirmId: null });
    };

    handleDelete = () => {
        const username = getStoredUsername();
        const { deleteConfirmId } = this.state;
        if (!username || !deleteConfirmId) return;

        this.setState({ isSaving: true });
        apiClient
            .delete(`/api/exchange_key_pair/${username}/${deleteConfirmId}`)
            .then(() => {
                this.setState({
                    notice: { type: "success", message: "삭제 완료" },
                    deleteConfirmId: null,
                });
                this.loadKeyPairs();
            })
            .catch(() => {
                this.setState({ notice: { type: "error", message: "삭제 실패" } });
            })
            .finally(() => {
                this.setState({ isSaving: false });
            });
    };

    // --- Render ---
    renderDesktopRow = (row) => {
        const { editingRowId, editValues, showSecrets, isSaving } = this.state;
        const isEditing = editingRowId === row.id;

        if (isEditing) {
            return (
                <tr key={row.id} className="border-b border-surface-border bg-surface-elevated">
                    <td className="px-2 py-3 text-left text-xs sm:px-3">
                        <span className="inline-flex items-center gap-1.5 text-content-muted">
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            {row.id}
                        </span>
                    </td>
                    <td className="px-2 py-3 sm:px-3">
                        <input
                            type="text"
                            value={editValues.strategy_name}
                            onChange={this.handleEditChange("strategy_name")}
                            placeholder="전략 이름 입력"
                            className="w-full rounded-lg border border-surface-border bg-surface px-3 py-1.5 text-xs text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="전략 이름"
                        />
                    </td>
                    <td className="px-2 py-3 sm:px-3">
                        <select
                            value={editValues.fiat_exchange}
                            onChange={this.handleEditChange("fiat_exchange")}
                            className="w-full rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-xs text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="현물 거래소"
                        >
                            <option value="">미사용</option>
                            <option value="upbit">Upbit</option>
                            <option value="korbit">Korbit</option>
                            <option value="bithumb">Bithumb</option>
                        </select>
                    </td>
                    <td className="px-2 py-3 sm:px-3">
                        <input
                            type="password"
                            value={editValues.fiat_api_key}
                            onChange={this.handleEditChange("fiat_api_key")}
                            placeholder="비워두면 기존 값 유지"
                            className="w-full rounded-lg border border-surface-border bg-surface px-3 py-1.5 text-xs font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="현물 API 키"
                        />
                    </td>
                    <td className="px-2 py-3 sm:px-3">
                        <input
                            type="password"
                            value={editValues.fiat_secret_key}
                            onChange={this.handleEditChange("fiat_secret_key")}
                            placeholder="비워두면 기존 값 유지"
                            className="w-full rounded-lg border border-surface-border bg-surface px-3 py-1.5 text-xs font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="현물 시크릿 키"
                        />
                    </td>
                    <td className="px-2 py-3 sm:px-3">
                        <select
                            value={editValues.risky_exchange}
                            onChange={this.handleEditChange("risky_exchange")}
                            className="w-full rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-xs text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="선물 거래소"
                        >
                            <option value="">미사용</option>
                            <option value="binance">Binance</option>
                        </select>
                    </td>
                    <td className="px-2 py-3 sm:px-3">
                        <input
                            type="password"
                            value={editValues.risky_api_key}
                            onChange={this.handleEditChange("risky_api_key")}
                            placeholder="비워두면 기존 값 유지"
                            className="w-full rounded-lg border border-surface-border bg-surface px-3 py-1.5 text-xs font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="선물 API 키"
                        />
                    </td>
                    <td className="px-2 py-3 sm:px-3">
                        <input
                            type="password"
                            value={editValues.risky_secret_key}
                            onChange={this.handleEditChange("risky_secret_key")}
                            placeholder="비워두면 기존 값 유지"
                            className="w-full rounded-lg border border-surface-border bg-surface px-3 py-1.5 text-xs font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                            aria-label="선물 시크릿 키"
                        />
                    </td>
                    <td className="px-2 py-3 text-center sm:px-3">
                        <div className="flex items-center justify-center gap-2">
                            <button
                                type="button"
                                onClick={this.saveEdit}
                                disabled={isSaving}
                                className="inline-flex items-center gap-1 rounded-lg bg-brand/20 border border-brand/30 px-3 py-1.5 text-xs font-semibold text-brand-light shadow-sm transition-all hover:bg-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label="저장"
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        저장중
                                    </>
                                ) : (
                                    "저장"
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={this.cancelEdit}
                                disabled={isSaving}
                                className="rounded-lg border border-surface-border bg-surface-card px-3 py-1.5 text-xs font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label="취소"
                            >
                                취소
                            </button>
                        </div>
                    </td>
                </tr>
            );
        }

        const isRevealed = showSecrets[row.id];
        return (
            <tr key={row.id} className="border-b border-surface-border transition-colors hover:bg-surface-elevated">
                <td className="px-2 py-3 text-left text-xs leading-4 text-content-muted sm:px-3">{row.id}</td>
                <td className="px-2 py-3 text-left text-xs leading-5 font-medium text-content sm:px-3">
                    {row.strategy_name || "default"}
                </td>
                <td className="px-2 py-3 text-center sm:px-3">
                    <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getColorTag(row.fiat_exchange)}`}>
                        {row.fiat_exchange || "미설정"}
                    </span>
                </td>
                <td className="px-2 py-3 text-center text-xs leading-4 sm:px-3">
                    <code className={`inline-block max-w-full truncate rounded px-2 py-0.5 font-mono ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                        {this.getDisplayValue(row, "fiat_api_key")}
                    </code>
                </td>
                <td className="px-2 py-3 text-center text-xs leading-4 sm:px-3">
                    <code className={`inline-block max-w-full truncate rounded px-2 py-0.5 font-mono ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                        {this.getDisplayValue(row, "fiat_secret_key")}
                    </code>
                </td>
                <td className="px-2 py-3 text-center sm:px-3">
                    <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getColorTag(row.risky_exchange)}`}>
                        {row.risky_exchange || "미설정"}
                    </span>
                </td>
                <td className="px-2 py-3 text-center text-xs leading-4 sm:px-3">
                    <code className={`inline-block max-w-full truncate rounded px-2 py-0.5 font-mono ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                        {this.getDisplayValue(row, "risky_api_key")}
                    </code>
                </td>
                <td className="px-2 py-3 text-center text-xs leading-4 sm:px-3">
                    <code className={`inline-block max-w-full truncate rounded px-2 py-0.5 font-mono ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                        {this.getDisplayValue(row, "risky_secret_key")}
                    </code>
                </td>
                <td className="px-2 py-3 text-center sm:px-3">
                    <div className="flex items-center justify-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => this.startEdit(row)}
                            className="inline-flex items-center gap-1 rounded-lg border border-surface-border bg-surface-card px-2.5 py-1.5 text-xs font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover"
                            aria-label="수정"
                        >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            수정
                        </button>
                        <button
                            type="button"
                            onClick={() => this.fetchSecrets(row.id)}
                            className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold shadow-sm transition-all ${
                                isRevealed
                                    ? "border-warning bg-warning-subtle text-warning"
                                    : "border-surface-border bg-surface-card text-content-muted hover:bg-surface-hover"
                            }`}
                            title={isRevealed ? "비밀값 숨기기" : "비밀값 보기"}
                            aria-label={isRevealed ? "비밀값 숨기기" : "비밀값 보기"}
                        >
                            {isRevealed ? (
                                <>
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                    </svg>
                                    숨김
                                </>
                            ) : (
                                <>
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    보기
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => this.confirmDelete(row.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-loss/30 bg-surface-card px-2.5 py-1.5 text-xs font-semibold text-loss shadow-sm transition-colors hover:bg-loss-subtle"
                            aria-label="삭제"
                        >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            삭제
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    renderMobileCard = (row) => {
        const { editingRowId, editValues, showSecrets, isSaving } = this.state;
        const isEditing = editingRowId === row.id;
        const isRevealed = showSecrets[row.id];

        if (isEditing) {
            return (
                <div key={row.id} className="rounded-xl border-2 border-brand/30 bg-surface-elevated p-4 mb-3">
                    <div className="mb-3 flex items-center gap-2 border-b border-surface-border pb-2">
                        <svg className="h-4 w-4 text-brand-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        <span className="text-sm font-semibold text-content">수정 모드</span>
                    </div>
                    <div className="space-y-3 text-sm">
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">전략 이름</label>
                            <input
                                type="text"
                                value={editValues.strategy_name}
                                onChange={this.handleEditChange("strategy_name")}
                                placeholder="전략 이름 입력"
                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                aria-label="전략 이름"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">현물 거래소</label>
                                <select
                                    value={editValues.fiat_exchange}
                                    onChange={this.handleEditChange("fiat_exchange")}
                                    className="w-full rounded-lg border border-surface-border bg-surface px-2 py-2 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                    aria-label="현물 거래소"
                                >
                                    <option value="">미사용</option>
                                    <option value="upbit">Upbit</option>
                                    <option value="korbit">Korbit</option>
                                    <option value="bithumb">Bithumb</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">선물 거래소</label>
                                <select
                                    value={editValues.risky_exchange}
                                    onChange={this.handleEditChange("risky_exchange")}
                                    className="w-full rounded-lg border border-surface-border bg-surface px-2 py-2 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                    aria-label="선물 거래소"
                                >
                                    <option value="">미사용</option>
                                    <option value="binance">Binance</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">Fiat API Key</label>
                            <input
                                type="password"
                                value={editValues.fiat_api_key}
                                onChange={this.handleEditChange("fiat_api_key")}
                                placeholder="비워두면 기존 값 유지"
                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                aria-label="현물 API 키"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">Fiat Secret Key</label>
                            <input
                                type="password"
                                value={editValues.fiat_secret_key}
                                onChange={this.handleEditChange("fiat_secret_key")}
                                placeholder="비워두면 기존 값 유지"
                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                aria-label="현물 시크릿 키"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">Risky API Key</label>
                            <input
                                type="password"
                                value={editValues.risky_api_key}
                                onChange={this.handleEditChange("risky_api_key")}
                                placeholder="비워두면 기존 값 유지"
                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                aria-label="선물 API 키"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-content-muted">Risky Secret Key</label>
                            <input
                                type="password"
                                value={editValues.risky_secret_key}
                                onChange={this.handleEditChange("risky_secret_key")}
                                placeholder="비워두면 기존 값 유지"
                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                aria-label="선물 시크릿 키"
                            />
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button
                                type="button"
                                onClick={this.saveEdit}
                                disabled={isSaving}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand/20 border border-brand/30 px-4 py-2.5 text-sm font-semibold text-brand-light shadow-sm transition-all hover:bg-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label="저장"
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        저장중...
                                    </>
                                ) : (
                                    "저장"
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={this.cancelEdit}
                                disabled={isSaving}
                                className="flex-1 rounded-lg border border-surface-border bg-surface-card px-4 py-2.5 text-sm font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label="취소"
                            >
                                취소
                            </button>
                        </div>
                        <p className="rounded bg-surface px-2 py-1.5 text-xs text-content-muted">
                            비어있는 비밀값 필드는 기존 값이 유지됩니다.
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div key={row.id} className="rounded-xl border border-surface-border bg-surface-card p-4 mb-3 shadow-card transition-shadow hover:shadow-md">
                <div className="mb-3 flex items-start justify-between border-b border-surface-border pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getColorTag(row.fiat_exchange)}`}>
                            {row.fiat_exchange || "미설정"}
                        </span>
                        <span className="text-content-muted">→</span>
                        <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getColorTag(row.risky_exchange)}`}>
                            {row.risky_exchange || "미설정"}
                        </span>
                    </div>
                    <div className="flex flex-shrink-0 gap-1.5">
                        <button
                            type="button"
                            onClick={() => this.startEdit(row)}
                            className="inline-flex items-center gap-1 rounded-lg border border-surface-border bg-surface-card px-2.5 py-1.5 text-xs font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover"
                            aria-label="수정"
                        >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={() => this.fetchSecrets(row.id)}
                            className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold shadow-sm transition-all ${
                                isRevealed
                                    ? "border-warning bg-warning-subtle text-warning"
                                    : "border-surface-border bg-surface-card text-content-muted hover:bg-surface-hover"
                            }`}
                            aria-label={isRevealed ? "비밀값 숨기기" : "비밀값 보기"}
                        >
                            {isRevealed ? (
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                            ) : (
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => this.confirmDelete(row.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-loss/30 bg-surface-card px-2.5 py-1.5 text-xs font-semibold text-loss shadow-sm transition-colors hover:bg-loss-subtle"
                            aria-label="삭제"
                        >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="space-y-2.5 text-xs">
                    <div>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-content-muted">Fiat API Key</div>
                        <code className={`block w-full truncate rounded-md px-2.5 py-1.5 font-mono text-xs ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                            {this.getDisplayValue(row, "fiat_api_key")}
                        </code>
                    </div>
                    <div>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-content-muted">Fiat Secret</div>
                        <code className={`block w-full truncate rounded-md px-2.5 py-1.5 font-mono text-xs ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                            {this.getDisplayValue(row, "fiat_secret_key")}
                        </code>
                    </div>
                    <div>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-content-muted">Risky API Key</div>
                        <code className={`block w-full truncate rounded-md px-2.5 py-1.5 font-mono text-xs ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                            {this.getDisplayValue(row, "risky_api_key")}
                        </code>
                    </div>
                    <div>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-content-muted">Risky Secret</div>
                        <code className={`block w-full truncate rounded-md px-2.5 py-1.5 font-mono text-xs ${isRevealed ? "bg-surface-elevated text-content" : "bg-surface text-content-muted"}`}>
                            {this.getDisplayValue(row, "risky_secret_key")}
                        </code>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const isMobile = isMobileViewport();
        const { hideListRender } = this.props;
        const {
            listKeyPair, isAddModalVisible, isSaving, notice,
            addFormValues, enableFiat, enableRisky, isLoading, fetchingSecrets,
            deleteConfirmId,
        } = this.state;

        // Group by strategy
        const grouped = {};
        listKeyPair.forEach((row) => {
            const sn = row.strategy_name || "default";
            if (!grouped[sn]) grouped[sn] = [];
            grouped[sn].push(row);
        });
        const strategyNames = Object.keys(grouped).sort();

        // hideListRender=true: V2 위임 모드 — 리스트/테이블은 부모가 렌더,
        // 여기서는 알림 배너 + Add/Delete/Secret 모달만 렌더한다.
        if (hideListRender) {
            return (
                <>
                    {fetchingSecrets && (
                        <div className="fixed inset-0 z-40 flex items-center justify-center bg-overlay backdrop-blur-sm">
                            <div className="rounded-xl bg-surface-card px-6 py-4 shadow-xl border border-surface-border">
                                <div className="flex items-center gap-3">
                                    <svg className="h-5 w-5 animate-spin text-brand-light" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="text-sm font-medium text-content">비밀값 불러오는 중...</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {notice && (
                        <div
                            className={`fixed left-1/2 top-4 z-50 flex -translate-x-1/2 items-center gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg ${
                                notice.type === "error"
                                    ? "border-loss/30 bg-loss-subtle text-loss"
                                    : "border-gain/30 bg-gain-subtle text-gain"
                            }`}
                            role="alert"
                        >
                            {notice.type === "error" ? (
                                <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            )}
                            <span className="flex-1">{notice.message}</span>
                            <button
                                type="button"
                                onClick={() => this.setState({ notice: null })}
                                className="flex-shrink-0 rounded p-0.5 transition-colors hover:bg-surface-hover"
                                aria-label="알림 닫기"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* Add Modal */}
                    {isAddModalVisible && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
                            <div className={`w-full rounded-2xl bg-surface-card border border-surface-border shadow-2xl animate-scale-in ${isMobile ? "max-w-md" : "max-w-2xl"}`}>
                                <div className="flex items-center justify-between border-b border-surface-border px-6 py-4">
                                    <h3 className="text-lg font-bold text-content">새 API Key 추가</h3>
                                    <button
                                        type="button"
                                        onClick={this.closeAddModal}
                                        className="rounded-lg p-1.5 text-content-muted transition-colors hover:bg-surface-hover hover:text-content-secondary"
                                        aria-label="모달 닫기"
                                    >
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="px-6 pt-4">
                                    <div className="rounded-lg border border-loss/30 bg-loss-subtle p-4">
                                        <div className="flex gap-3">
                                            <svg className="h-5 w-5 flex-shrink-0 text-loss" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <div className="space-y-1 text-sm text-loss">
                                                <p className="font-semibold">보안 주의사항</p>
                                                <ul className="ml-4 list-disc space-y-0.5 text-xs">
                                                    <li>제공 받은 Server IP 주소만 허용하세요</li>
                                                    <li>반드시 API Key 읽기 권한만 포함되어야 합니다</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 grid gap-4 px-6 sm:grid-cols-2">
                                    <div className="space-y-2 sm:col-span-2">
                                        <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">
                                            전략 이름 <span className="text-loss">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={addFormValues.strategyName}
                                            onChange={this.handleAddChange("strategyName")}
                                            placeholder="예: detlafx, 알파트레이딩"
                                            className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                            aria-required="true"
                                        />
                                    </div>

                                    <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                                        <label className="flex items-center gap-2.5 text-sm text-content-secondary">
                                            <input
                                                type="checkbox"
                                                checked={enableFiat}
                                                onChange={(e) => this.setState({ enableFiat: e.target.checked })}
                                                className="h-4 w-4 rounded border-surface-border bg-surface text-brand transition-colors focus:ring-2 focus:ring-brand/20"
                                            />
                                            <span className="font-medium">현물 (Fiat) 거래소 사용</span>
                                        </label>
                                        <label className="flex items-center gap-2.5 text-sm text-content-secondary">
                                            <input
                                                type="checkbox"
                                                checked={enableRisky}
                                                onChange={(e) => this.setState({ enableRisky: e.target.checked })}
                                                className="h-4 w-4 rounded border-surface-border bg-surface text-brand transition-colors focus:ring-2 focus:ring-brand/20"
                                            />
                                            <span className="font-medium">선물 (Risky) 거래소 사용</span>
                                        </label>
                                    </div>

                                    {enableFiat && (
                                        <>
                                            <div className="space-y-2">
                                                <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">
                                                    Fiat Exchange <span className="text-loss">*</span>
                                                </label>
                                                <select
                                                    value={addFormValues.fiatExchange}
                                                    onChange={this.handleAddChange("fiatExchange")}
                                                    className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                    aria-required="true"
                                                >
                                                    <option value="">선택하세요</option>
                                                    <option value="upbit">Upbit</option>
                                                    <option value="korbit">Korbit</option>
                                                    <option value="bithumb">Bithumb</option>
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Fiat API Key</label>
                                                <input
                                                    type="password"
                                                    value={addFormValues.fiatApiKey}
                                                    onChange={this.handleAddChange("fiatApiKey")}
                                                    className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                />
                                            </div>
                                            <div className="space-y-2 sm:col-span-2">
                                                <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Fiat Secret Key</label>
                                                <input
                                                    type="password"
                                                    value={addFormValues.fiatSecretKey}
                                                    onChange={this.handleAddChange("fiatSecretKey")}
                                                    className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {enableRisky && (
                                        <>
                                            <div className="space-y-2">
                                                <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">
                                                    Risky Exchange <span className="text-loss">*</span>
                                                </label>
                                                <select
                                                    value={addFormValues.riskyExchange}
                                                    onChange={this.handleAddChange("riskyExchange")}
                                                    className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                    aria-required="true"
                                                >
                                                    <option value="binance">Binance</option>
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Risky API Key</label>
                                                <input
                                                    type="password"
                                                    value={addFormValues.riskyApiKey}
                                                    onChange={this.handleAddChange("riskyApiKey")}
                                                    className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                />
                                            </div>
                                            <div className="space-y-2 sm:col-span-2">
                                                <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Risky Secret Key</label>
                                                <input
                                                    type="password"
                                                    value={addFormValues.riskySecretKey}
                                                    onChange={this.handleAddChange("riskySecretKey")}
                                                    className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end gap-3 border-t border-surface-border px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={this.closeAddModal}
                                        disabled={isSaving}
                                        className="rounded-lg border border-surface-border bg-surface-card px-5 py-2.5 text-sm font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label="취소"
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="button"
                                        onClick={this.handleAddSubmit}
                                        disabled={isSaving}
                                        className="inline-flex items-center gap-2 rounded-lg bg-brand/20 border border-brand/30 px-5 py-2.5 text-sm font-semibold text-brand-light shadow-sm transition-all hover:bg-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label="저장"
                                    >
                                        {isSaving ? (
                                            <>
                                                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                저장 중...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                저장
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {deleteConfirmId && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
                            <div className="w-full max-w-md rounded-2xl bg-surface-card border border-surface-border p-6 shadow-2xl animate-scale-in">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-loss-subtle">
                                        <svg className="h-6 w-6 text-loss" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-content">키 페어 삭제</h3>
                                        <p className="mt-2 text-sm text-content-secondary">
                                            정말로 이 API Key Pair를 삭제하시겠습니까?
                                            <br />
                                            <span className="font-semibold text-loss">이 작업은 되돌릴 수 없습니다.</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={this.cancelDelete}
                                        disabled={isSaving}
                                        className="rounded-lg border border-surface-border bg-surface-card px-4 py-2.5 text-sm font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label="취소"
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="button"
                                        onClick={this.handleDelete}
                                        disabled={isSaving}
                                        className="inline-flex items-center gap-2 rounded-lg bg-loss px-4 py-2.5 text-sm font-semibold text-content-inverse shadow-sm transition-colors hover:bg-loss-dark disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label="삭제"
                                    >
                                        {isSaving ? (
                                            <>
                                                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                삭제 중...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                삭제
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            );
        }

        return (
            <div className="space-y-4">
                {fetchingSecrets && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center bg-overlay backdrop-blur-sm">
                        <div className="rounded-xl bg-surface-card px-6 py-4 shadow-xl border border-surface-border">
                            <div className="flex items-center gap-3">
                                <svg className="h-5 w-5 animate-spin text-brand-light" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-sm font-medium text-content">비밀값 불러오는 중...</span>
                            </div>
                        </div>
                    </div>
                )}

                {notice && (
                    <div
                        className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm shadow-sm ${
                            notice.type === "error"
                                ? "border-loss/30 bg-loss-subtle text-loss"
                                : "border-gain/30 bg-gain-subtle text-gain"
                        }`}
                        role="alert"
                    >
                        {notice.type === "error" ? (
                            <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                        <span className="flex-1">{notice.message}</span>
                        <button
                            type="button"
                            onClick={() => this.setState({ notice: null })}
                            className="flex-shrink-0 rounded p-0.5 transition-colors hover:bg-surface-hover"
                            aria-label="알림 닫기"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}

                <div className="rounded-2xl border border-surface-border bg-surface-card p-5 shadow-card">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-border pb-4">
                        <div>
                            <h2 className="text-base font-bold text-content">보유 Key Pair</h2>
                            <p className="mt-0.5 text-xs text-content-muted">현재 등록된 거래소 키를 확인하고 수정하세요.</p>
                        </div>
                        <button
                            type="button"
                            onClick={this.openAddModal}
                            disabled={isLoading}
                            className="inline-flex items-center gap-2 rounded-lg bg-brand/20 border border-brand/30 px-4 py-2.5 text-sm font-semibold text-brand-light shadow-sm transition-colors hover:bg-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="새 Key Pair 추가"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            새 Key Pair 추가
                        </button>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <svg className="mx-auto h-10 w-10 animate-spin text-brand-light" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <p className="mt-3 text-sm font-medium text-content-muted">데이터 로드 중...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Mobile */}
                            {isMobile && (
                        <div className="mt-4 space-y-4">
                            {strategyNames.map((strategyName) => (
                                <div key={strategyName}>
                                    <div className="mb-3 flex items-center gap-2 rounded-lg bg-surface-elevated px-3 py-2">
                                        <svg className="h-4 w-4 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                        <span className="text-sm font-bold text-content">전략: {strategyName}</span>
                                    </div>
                                    {grouped[strategyName].map((row) => this.renderMobileCard(row))}
                                </div>
                            ))}
                            {listKeyPair.length === 0 && (
                                <EmptyState title="등록된 Key Pair가 없습니다" description="위 버튼을 눌러 새로운 키를 추가하세요" />
                            )}
                        </div>
                    )}

                    {/* Desktop */}
                    {!isMobile && (
                        <div className="mt-4 overflow-x-auto rounded-lg border border-surface-border">
                            <table className="w-full min-w-[900px] table-fixed border-collapse text-xs md:min-w-[960px]">
                                <thead className="bg-surface-elevated text-xs font-bold uppercase tracking-wider text-content-secondary">
                                    <tr>
                                        <th className="px-2 py-3 text-left sm:px-3">ID</th>
                                        <th className="px-2 py-3 text-left sm:px-3">전략</th>
                                        <th className="px-2 py-3 text-center sm:px-3">현물</th>
                                        <th className="px-2 py-3 text-center sm:px-3">Fiat API Key</th>
                                        <th className="px-2 py-3 text-center sm:px-3">Fiat Secret</th>
                                        <th className="px-2 py-3 text-center sm:px-3">선물</th>
                                        <th className="px-2 py-3 text-center sm:px-3">Risky API Key</th>
                                        <th className="px-2 py-3 text-center sm:px-3">Risky Secret</th>
                                        <th className="px-2 py-3 text-center sm:px-3">작업</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-surface-card">
                                    {strategyNames.map((strategyName) => (
                                        <React.Fragment key={strategyName}>
                                            <tr className="bg-surface-elevated/50">
                                                <td colSpan="9" className="px-3 py-2.5 text-left sm:px-3">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="h-3.5 w-3.5 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                        </svg>
                                                        <span className="text-sm font-bold text-content">전략: {strategyName}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {grouped[strategyName].map((row) => this.renderDesktopRow(row))}
                                        </React.Fragment>
                                    ))}
                                    {listKeyPair.length === 0 && (
                                        <tr>
                                            <td colSpan="9">
                                                <EmptyState title="등록된 Key Pair가 없습니다" description="위 버튼을 눌러 새로운 키를 추가하세요" />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <div className="rounded-b-lg bg-surface-elevated px-4 py-2.5">
                                <p className="flex items-start gap-2 text-xs text-content-muted">
                                    <svg className="h-4 w-4 flex-shrink-0 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>수정 시 비밀값(API Key, Secret Key) 필드를 비워두면 기존 값이 유지됩니다.</span>
                                </p>
                            </div>
                        </div>
                    )}
                        </>
                    )}
                </div>

                {/* Add Modal */}
                {isAddModalVisible && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
                        <div className={`w-full rounded-2xl bg-surface-card border border-surface-border shadow-2xl animate-scale-in ${isMobile ? "max-w-md" : "max-w-2xl"}`}>
                            <div className="flex items-center justify-between border-b border-surface-border px-6 py-4">
                                <h3 className="text-lg font-bold text-content">새 API Key 추가</h3>
                                <button
                                    type="button"
                                    onClick={this.closeAddModal}
                                    className="rounded-lg p-1.5 text-content-muted transition-colors hover:bg-surface-hover hover:text-content-secondary"
                                    aria-label="모달 닫기"
                                >
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="px-6 pt-4">
                                <div className="rounded-lg border border-loss/30 bg-loss-subtle p-4">
                                    <div className="flex gap-3">
                                        <svg className="h-5 w-5 flex-shrink-0 text-loss" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <div className="space-y-1 text-sm text-loss">
                                            <p className="font-semibold">보안 주의사항</p>
                                            <ul className="ml-4 list-disc space-y-0.5 text-xs">
                                                <li>제공 받은 Server IP 주소만 허용하세요</li>
                                                <li>반드시 API Key 읽기 권한만 포함되어야 합니다</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 grid gap-4 px-6 sm:grid-cols-2">
                                <div className="space-y-2 sm:col-span-2">
                                    <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">
                                        전략 이름 <span className="text-loss">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={addFormValues.strategyName}
                                        onChange={this.handleAddChange("strategyName")}
                                        placeholder="예: detlafx, 알파트레이딩"
                                        className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                        aria-required="true"
                                    />
                                </div>

                                <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                                    <label className="flex items-center gap-2.5 text-sm text-content-secondary">
                                        <input
                                            type="checkbox"
                                            checked={enableFiat}
                                            onChange={(e) => this.setState({ enableFiat: e.target.checked })}
                                            className="h-4 w-4 rounded border-surface-border bg-surface text-brand transition-colors focus:ring-2 focus:ring-brand/20"
                                        />
                                        <span className="font-medium">현물 (Fiat) 거래소 사용</span>
                                    </label>
                                    <label className="flex items-center gap-2.5 text-sm text-content-secondary">
                                        <input
                                            type="checkbox"
                                            checked={enableRisky}
                                            onChange={(e) => this.setState({ enableRisky: e.target.checked })}
                                            className="h-4 w-4 rounded border-surface-border bg-surface text-brand transition-colors focus:ring-2 focus:ring-brand/20"
                                        />
                                        <span className="font-medium">선물 (Risky) 거래소 사용</span>
                                    </label>
                                </div>

                                {enableFiat && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">
                                                Fiat Exchange <span className="text-loss">*</span>
                                            </label>
                                            <select
                                                value={addFormValues.fiatExchange}
                                                onChange={this.handleAddChange("fiatExchange")}
                                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                aria-required="true"
                                            >
                                                <option value="">선택하세요</option>
                                                <option value="upbit">Upbit</option>
                                                <option value="korbit">Korbit</option>
                                                <option value="bithumb">Bithumb</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Fiat API Key</label>
                                            <input
                                                type="password"
                                                value={addFormValues.fiatApiKey}
                                                onChange={this.handleAddChange("fiatApiKey")}
                                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                            />
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Fiat Secret Key</label>
                                            <input
                                                type="password"
                                                value={addFormValues.fiatSecretKey}
                                                onChange={this.handleAddChange("fiatSecretKey")}
                                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                            />
                                        </div>
                                    </>
                                )}

                                {enableRisky && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">
                                                Risky Exchange <span className="text-loss">*</span>
                                            </label>
                                            <select
                                                value={addFormValues.riskyExchange}
                                                onChange={this.handleAddChange("riskyExchange")}
                                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                                aria-required="true"
                                            >
                                                <option value="binance">Binance</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Risky API Key</label>
                                            <input
                                                type="password"
                                                value={addFormValues.riskyApiKey}
                                                onChange={this.handleAddChange("riskyApiKey")}
                                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                            />
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <label className="block text-xs font-bold uppercase tracking-wider text-content-secondary">Risky Secret Key</label>
                                            <input
                                                type="password"
                                                value={addFormValues.riskySecretKey}
                                                onChange={this.handleAddChange("riskySecretKey")}
                                                className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2.5 text-sm font-mono text-content shadow-sm transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="mt-6 flex justify-end gap-3 border-t border-surface-border px-6 py-4">
                                <button
                                    type="button"
                                    onClick={this.closeAddModal}
                                    disabled={isSaving}
                                    className="rounded-lg border border-surface-border bg-surface-card px-5 py-2.5 text-sm font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-label="취소"
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    onClick={this.handleAddSubmit}
                                    disabled={isSaving}
                                    className="inline-flex items-center gap-2 rounded-lg bg-brand/20 border border-brand/30 px-5 py-2.5 text-sm font-semibold text-brand-light shadow-sm transition-all hover:bg-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-label="저장"
                                >
                                    {isSaving ? (
                                        <>
                                            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            저장 중...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            저장
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirmId && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
                        <div className="w-full max-w-md rounded-2xl bg-surface-card border border-surface-border p-6 shadow-2xl animate-scale-in">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-loss-subtle">
                                    <svg className="h-6 w-6 text-loss" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-content">키 페어 삭제</h3>
                                    <p className="mt-2 text-sm text-content-secondary">
                                        정말로 이 API Key Pair를 삭제하시겠습니까?
                                        <br />
                                        <span className="font-semibold text-loss">이 작업은 되돌릴 수 없습니다.</span>
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={this.cancelDelete}
                                    disabled={isSaving}
                                    className="rounded-lg border border-surface-border bg-surface-card px-4 py-2.5 text-sm font-semibold text-content-secondary shadow-sm transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-label="취소"
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    onClick={this.handleDelete}
                                    disabled={isSaving}
                                    className="inline-flex items-center gap-2 rounded-lg bg-loss px-4 py-2.5 text-sm font-semibold text-content-inverse shadow-sm transition-colors hover:bg-loss-dark disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-label="삭제"
                                >
                                    {isSaving ? (
                                        <>
                                            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            삭제 중...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            삭제
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default APIKeyEditable;
